import { MusicTheory } from './music-theory.js';
import { StatisticsManager } from './statistics-manager.js';
import { AudioManager } from './audio-manager.js';
import { UIManager } from './ui-manager.js';
import { Challenge, ChallengeType, ChallengeStrategy } from './challenge-types.js';
import { ForwardChallengeStrategy } from './forward-challenge-strategy.js';
import { InverseChallengeStrategy } from './inverse-challenge-strategy.js';

interface TranscriptionResult {
  parsedNote: string | null;
  ttsPromise?: Promise<void>; // Present only when TTS is active (incorrect answers)
}

export class ChallengeManager {
  private audioManager: AudioManager;
  private statsManager: StatisticsManager;
  private uiManager: UIManager;
  private currentChallenge: Challenge | null = null;
  private currentChallengeType: ChallengeType = ChallengeType.FORWARD;
  private challengeStrategy: ChallengeStrategy;

  constructor(audioManager: AudioManager, statsManager: StatisticsManager, uiManager: UIManager) {
    this.audioManager = audioManager;
    this.statsManager = statsManager;
    this.uiManager = uiManager;
    this.challengeStrategy = new ForwardChallengeStrategy();
  }

  /**
   * Get current challenge
   */
  getCurrentChallenge(): Challenge | null {
    return this.currentChallenge;
  }

  /**
   * Set the challenge type and update the strategy
   */
  setChallengeType(type: ChallengeType): void {
    this.currentChallengeType = type;
    
    switch (type) {
      case ChallengeType.FORWARD:
        this.challengeStrategy = new ForwardChallengeStrategy();
        break;
      case ChallengeType.INVERSE:
        this.challengeStrategy = new InverseChallengeStrategy();
        break;
      default:
        throw new Error(`Unknown challenge type: ${type}`);
    }
  }

  /**
   * Get the current challenge type
   */
  getCurrentChallengeType(): ChallengeType {
    return this.currentChallengeType;
  }

  /**
   * Generate a new challenge
   */
  generateNewChallenge(isRunning: boolean, isPaused: boolean): void {
    if (!isRunning || isPaused) return;

    // Use the current strategy to generate the challenge
    this.currentChallenge = this.challengeStrategy.generateChallenge();

    const displayText = this.challengeStrategy.formatDisplayText(this.currentChallenge);
    const speakText = this.challengeStrategy.formatSpeechText(this.currentChallenge);
    this.uiManager.setChallengeText(displayText);

    // Notify UI about the current key for manual input mode
    if ((this.uiManager as any).reactCallbacks?.setChallengeKey) {
      (this.uiManager as any).reactCallbacks.setChallengeKey(this.currentChallenge.key);
    }

    console.log(`New challenge: ${displayText} (Answer: ${this.currentChallenge.correctAnswer})`);

    // Speak the challenge
    this.speakChallenge(speakText, isRunning, isPaused);
  }

  /**
   * Generate challenge with specific key (from previous answer)
   */
  generateChallengeWithKey(key: string, isRunning: boolean, isPaused: boolean): void {
    if (!isRunning || isPaused) return;

    // Use the current strategy to generate the challenge with the specified key
    this.currentChallenge = this.challengeStrategy.generateChallenge(key);

    const displayText = this.challengeStrategy.formatDisplayText(this.currentChallenge);
    const speakText = this.challengeStrategy.formatSpeechText(this.currentChallenge);
    this.uiManager.setChallengeText(displayText);

    // Notify UI about the current key for manual input mode
    if ((this.uiManager as any).reactCallbacks?.setChallengeKey) {
      (this.uiManager as any).reactCallbacks.setChallengeKey(this.currentChallenge.key);
    }

    console.log(`Next challenge: ${displayText} (Answer: ${this.currentChallenge.correctAnswer})`);

    this.speakChallenge(speakText, isRunning, isPaused);
  }

  /**
   * Speak the current challenge
   */
  private async speakChallenge(text: string, _isRunning: boolean, _isPaused: boolean): Promise<void> {
    try {
      await this.audioManager.speak(text);
    } catch (error) {
      console.error('Failed to speak challenge:', error);
      // Fallback: trigger listening immediately if needed
      // This will be handled by the caller
    }
  }

  /**
    * Process transcription result from Web Speech API
    */
   async processTranscriptionResult(transcription: string | null, challengeStartTime: number | null, isProcessing: boolean): Promise<TranscriptionResult> {
     if (!this.currentChallenge || isProcessing) return { parsedNote: null };

     const responseTime = challengeStartTime ? Date.now() - challengeStartTime : 0;

     try {
       // Detect if we're on mobile for enhanced preprocessing
       const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
       
       // Parse the answer based on challenge type
       let parsedAnswer: string | null;
       if (this.currentChallenge.type === ChallengeType.FORWARD) {
         parsedAnswer = MusicTheory.parseNoteName(transcription, isMobile);
       } else {
         parsedAnswer = MusicTheory.parseKeyName(transcription, isMobile);
       }

       // Use strategy to validate the answer
       const isCorrect = this.challengeStrategy.validateAnswer(this.currentChallenge, parsedAnswer);

       console.log(`Transcription: "${transcription}" -> Parsed: "${parsedAnswer}" -> Correct: ${isCorrect}`);

       // Record statistics
       this.statsManager.recordAttempt(
         this.currentChallenge.key,
         this.currentChallenge.degree,
         isCorrect,
         responseTime,
         this.currentChallenge.type
       );

       // Display result with challenge type for mode-aware feedback
       this.uiManager.displayResult(parsedAnswer, isCorrect, responseTime, transcription, this.currentChallenge.correctAnswer, this.currentChallenge.type);

       // Update statistics display
       this.uiManager.updateDisplay();

       // Play sound effect for the result
       if (isCorrect) {
         this.audioManager.playCorrectSound().catch(err => console.log('Could not play correct sound:', err));
         return { parsedNote: parsedAnswer };
       } else {
         this.audioManager.playIncorrectSound().catch(err => console.log('Could not play incorrect sound:', err));
         // Use strategy to get the correct answer speech
         const correctAnswerSpeech = this.challengeStrategy.getCorrectAnswerSpeech(this.currentChallenge);
         const ttsPromise = this.audioManager.speak(correctAnswerSpeech).catch(err => console.log('Could not speak correct answer:', err));
         return { parsedNote: parsedAnswer, ttsPromise };
       }

     } catch (error) {
       console.error('Failed to process transcription result:', error);
       throw error;
     }
   }

  /**
   * Schedule the next challenge
   */
  scheduleNextChallenge(_parsedNote: string | null, isRunning: boolean, isPaused: boolean, callback: () => void): void {
    setTimeout(() => {
      if (!isRunning || isPaused) return;

      if (this.currentChallenge) {
        let nextKey: string;
        
        if (this.currentChallenge.type === ChallengeType.FORWARD) {
          // In forward mode, use the correct answer (note) as the new key
          const correctAnswer = this.currentChallenge.correctAnswer;
          nextKey = MusicTheory.CHROMATIC_TO_KEY[correctAnswer] || correctAnswer;
        } else {
          // In inverse mode, use the note from the question as the new key
          const noteFromQuestion = this.currentChallenge.note;
          nextKey = MusicTheory.CHROMATIC_TO_KEY[noteFromQuestion] || noteFromQuestion;
        }
        
        this.generateChallengeWithKey(nextKey, isRunning, isPaused);
      } else {
        // Fallback to random challenge if no current challenge
        this.generateNewChallenge(isRunning, isPaused);
      }

      callback();
    }, 2500); // Slightly longer delay to read result
  }
}